import React from 'react'
import { Download, Loader2, ArrowRight, ArrowUpRight, SlidersHorizontal, Clock, MessageSquare } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Button from '../../../../components/ui/button'
import Dialog from '../../../../components/ui/dialog'
import { useEditorStore } from '../../state/editor-store'
import { useAuth } from '../../../../hooks/use-auth'
import { formatDuration } from '../../../../lib/utils'
import { PLANS, suggestTierForCredits, getPlanCreditsCap, minutesForCredits, type PlanId } from '../../../../lib/plans'

import EditorAudioPlayer from './editor-audio-player'
import { downloadFile, sanitizeFilename } from '../../../../lib/file-download'
import { markPodcastPlayedForCurrentUser } from '../../../../api/podcasts'
import type { Podcast } from '../../../../types/podcast'
import { track } from '../../../../lib/analytics/posthog'
import UpgradeDialog from '../../../../components/billing/upgrade-dialog'

export const EditorFooter: React.FC<{
  podcast?: Podcast
  currentLines: Array<{ audioFileUrl?: string | null; text?: string }>
  onSmartExport: () => void
  actionLoading: 'synth' | 'assemble' | null
  onOpenSettings?: () => void
  settingsOpen?: boolean
  onOpenChat?: () => void
  chatOpen?: boolean
}> = ({ podcast, currentLines, onSmartExport, actionLoading, onOpenSettings, settingsOpen, onOpenChat, chatOpen }) => {
  const navigate = useNavigate()
  const activity = useEditorStore((s) => s.activity)

  const synthesisActive = activity?.phase === 'synthesis' && activity.step !== 'line_completed'
  const assembleActive = activity?.phase === 'assemble'
  const allHaveAudio = currentLines.every(l => !!l.audioFileUrl?.trim())
  const hasEpisodeAudio = !!podcast?.audioFileUrl?.trim()

  // Episode audio availability

  const { user } = useAuth()
  const currentPlan = (user?.plan || 'free') as PlanId

  const estimatedCredits = podcast?.estimatedCreditsToSynthesize ?? 0

  const planCreditsCap = getPlanCreditsCap(currentPlan)
  const willExceed = estimatedCredits > 0 && estimatedCredits > planCreditsCap
  const suggested = suggestTierForCredits(estimatedCredits, currentPlan)

  const [showOutOfCredits, setShowOutOfCredits] = React.useState(false)
  const [showDownloadPaywall, setShowDownloadPaywall] = React.useState(false)

  const remaining = typeof user?.creditsRemaining === 'number' ? user.creditsRemaining : planCreditsCap

  // Out-of-credits dialog copy: paid users keep original encouragement, free users get upgrade prompt
  const isFreePlan = currentPlan === 'free'
  const outOfCreditsTitle = isFreePlan ? "You're out of free minutes" : "Almost there! You need more minutes"
  const outOfCreditsDescription = isFreePlan ? "Please upgrade to continue using the app." : undefined

  const handleGenerateClick = () => {
    // Note: when all lines have audio but the final episode isn't assembled yet,
    // we allow assembling even for free users. Download gating happens only on the
    // player's Download action.
    // First full run allowance: let users run up to 60 + (50 * 0.6) = 90 credits, leaving backend buffer intact
    // This keeps (never lets users consume the extra 50 credits), while before that it is looser
    if (!user?.hasGeneratedFullPodcastAudio) {
      const FIRST_FULL_RUN_ALLOWANCE = 60 + Math.round(50 * 0.6) // 90 credits
      if (!allHaveAudio && estimatedCredits > FIRST_FULL_RUN_ALLOWANCE) {
        setShowOutOfCredits(true)
        return
      }
      onSmartExport()
      return
    }

    if (!allHaveAudio && estimatedCredits > remaining) {
      setShowOutOfCredits(true)
      return
    }
    onSmartExport()
  }

  // When audio exists, the player's Download button should download the file directly
  const handleDownloadClick = React.useCallback(() => {
    const isPaidUser = (user?.plan ?? 'free') !== 'free'
    if (!isPaidUser) {
      setShowDownloadPaywall(true)
      return
    }
    if (podcast?.audioFileUrl) {
      const rawTitle = (podcast.title || 'podcast-episode').trim()
      const safe = sanitizeFilename(rawTitle)
      const filename = `${safe}.mp3`
      void downloadFile(podcast.audioFileUrl, filename).then((ok) => {
        if (ok) {
          // Track successful episode download
          track('podcast_downloaded')
          void markPodcastPlayedForCurrentUser(podcast.id).catch(() => { /* noop */ })
        }
      })
    } else {
      // Fallback: trigger generation/assemble flow
      handleGenerateClick()
    }
  }, [podcast, currentPlan, user?.plan])

  // (Playback is handled in EditorAudioPlayer)

  // Hide footer loader for single-line generation (backend sets activity with meta.lineId)
  const isSingleLineSynthesis = synthesisActive && !!activity?.meta?.lineId
  // Show loader instantly for bulk synthesis/assemble via local actionLoading, or when activity says so
  const showFooterLoader = (
    actionLoading === 'synth' ||
    actionLoading === 'assemble' ||
    assembleActive ||
    (synthesisActive && !isSingleLineSynthesis)
  )

  const loaderMessage = (() => {
    if (actionLoading === 'assemble' || assembleActive) {
      return activity?.message
    }
    if (actionLoading === 'synth' || (synthesisActive && !isSingleLineSynthesis)) {
      return activity?.message ?? 'Generating audio…'
    }
    return ''
  })()

  return (
    <div className="fixed sm:sticky inset-x-0 bottom-0 border-t border-black/10 bg-white z-40 sm:z-auto overflow-x-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-3 sm:py-4 pb-[calc(theme(spacing.3)+env(safe-area-inset-bottom))] flex items-center gap-3 md:gap-6">
        <div className="min-h-[28px] flex items-center">
          {showFooterLoader && (
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center gap-2 text-xs text-foreground/70">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="truncate max-w-[200px] sm:max-w-[260px]" title={loaderMessage}>{loaderMessage}</span>
              </div>
            </div>
          )}
          {!showFooterLoader && (
            <>
              {/* Mobile settings open button (only when closed) */}
              {onOpenSettings && !settingsOpen && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onOpenSettings}
                  className="lg:hidden inline-flex items-center gap-2 h-10 px-3 rounded-full"
                  aria-haspopup="dialog"
                  aria-expanded={settingsOpen}
                  aria-controls="podcast-settings-dialog"
                >
                  <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                  <span className="text-xs font-medium">Settings</span>
                </Button>
              )}
            </>
          )}
        </div>
        <div className={`flex items-center gap-3 overflow-visible flex-1 min-w-0 ${hasEpisodeAudio ? 'justify-center sm:justify-start' : 'justify-end'}`}>
          {/* Chat button (only when closed) */}
          {onOpenChat && !chatOpen && (
            <Button
              type="button"
              variant="secondary"
              onClick={onOpenChat}
              className="inline-flex items-center gap-2 h-10 px-3 rounded-full sm:hidden"
              aria-label="Open script assistant chat"
            >
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              <span className="text-xs font-medium">Chat</span>
            </Button>
          )}
          {/* Credit cost / estimation only relevant before any synthesis; hide when only assembly remains */}
          {!hasEpisodeAudio && !allHaveAudio && (
            <div className="hidden sm:flex items-center gap-2 text-xs text-foreground/60" title="1 second = 1 credit">
              {estimatedCredits > 0 ? (
                <span>~{minutesForCredits(estimatedCredits)} minutes</span>
              ) : (
                <span>1 sec = 1 credit</span>
              )}
            </div>
          )}
          {/* Separate clock pill removed once integrated player shows duration */}
          {podcast?.durationSeconds && !hasEpisodeAudio && (
            <>
              <span className="text-foreground/50 hidden sm:inline">·</span>
              <span className="hidden sm:inline-flex items-center gap-1 text-xs text-foreground/60" title="Actual duration of generated audio">
                <Clock className="h-3.5 w-3.5" />
                {formatDuration(podcast.durationSeconds)}
              </span>
            </>
          )}
          {willExceed && user?.hasGeneratedFullPodcastAudio && (
            <button
              className="hidden lg:inline-flex items-center gap-1 rounded-full bg-brand-50 text-brand-700 hover:bg-brand-100 px-3 py-1 text-xs font-medium border border-brand-200 cursor-pointer flex-shrink-0"
              onClick={() => navigate('/billing')}
            >
              Need more? Upgrade to {PLANS[suggested].name}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          {/* Action / Player cluster */}
          {!hasEpisodeAudio && (
            <Button
              onClick={handleGenerateClick}
              disabled={actionLoading === 'assemble' || actionLoading === 'synth' || synthesisActive || assembleActive}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {actionLoading === 'assemble' || assembleActive ? 'Assembling…' : allHaveAudio ? `Assemble episode` : `Generate all audio`}
            </Button>
          )}
          {hasEpisodeAudio && podcast?.audioFileUrl && (
            <div className="flex-1 min-w-0 flex justify-center sm:justify-start">
              <EditorAudioPlayer
                audioUrl={podcast.audioFileUrl}
                durationSeconds={podcast.durationSeconds}
                fileName={(podcast.title ? podcast.title.replace(/\s+/g, '-') : 'podcast-episode') + '.mp3'}
                onDownload={handleDownloadClick}
                podcastId={podcast.id}
              />
            </div>
          )}
        </div>
      </div>

      {/* Out-of-credits dialog */}
      <Dialog
        open={showOutOfCredits}
        onOpenChange={setShowOutOfCredits}
        title={outOfCreditsTitle}
        description={outOfCreditsDescription}
        size="md"
        heightMode="auto"
      >
        <div className="space-y-5">
          {/* Social proof */}
          <div className="flex items-center justify-center gap-2 text-xs text-foreground/60">
            <div className="flex -space-x-1.5">
              <div className="h-5 w-5 rounded-full ring-2 ring-white bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-[9px] font-semibold text-white">A</div>
              <div className="h-5 w-5 rounded-full ring-2 ring-white bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-[9px] font-semibold text-white">S</div>
              <div className="h-5 w-5 rounded-full ring-2 ring-white bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-[9px] font-semibold text-white">M</div>
              <div className="h-5 w-5 rounded-full ring-2 ring-white bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-[9px] font-semibold text-white">+</div>
            </div>
            <span className="font-medium">Trusted by 1000+ creators · 2000+ podcasts generated</span>
          </div>

          {/* Usage comparison */}
          <div className="rounded-xl border border-black/10 bg-gradient-to-br from-black/2.5 to-brand-50/20 p-4">
            <div className="flex items-center justify-between text-sm mb-3">
              <span className="text-foreground/70">You need</span>
              <span className="font-semibold text-foreground text-base">≈{minutesForCredits(estimatedCredits)} minutes</span>
            </div>
            <div className="flex items-center justify-between text-sm pb-3 border-b border-black/10">
              <span className="text-foreground/70">Currently available</span>
              <span className="font-medium text-foreground/50">{minutesForCredits(remaining)} minutes</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-3">
              <span className="text-foreground/70 font-medium">Missing</span>
              <span className="font-semibold text-brand-600 text-base">≈{minutesForCredits(Math.max(0, estimatedCredits - remaining))} minutes</span>
            </div>
          </div>

          {/* Value proposition */}
          <div className="rounded-xl bg-brand-50/50 border border-brand-200/50 p-4">
            <div className="flex items-start gap-3">
              <div>
                <h4 className="font-semibold text-foreground text-sm mb-1">
                  {PLANS[suggested].name} plan unlocks
                </h4>
                <ul className="space-y-1.5 text-sm text-foreground/70">
                  <li className="flex items-center gap-2">
                    <span className="text-brand-600">✓</span>
                    <span><strong className="text-foreground font-medium">{minutesForCredits(getPlanCreditsCap(suggested))} minutes/month</strong> ({Math.floor(getPlanCreditsCap(suggested) / estimatedCredits)}+ episodes like this)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-brand-600">✓</span>
                    <span>Cancel anytime, keep all your podcasts</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col gap-2">
            <button
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 h-11 px-5 text-[15px] shadow-sm"
              onClick={() => navigate('/billing')}
            >
              Upgrade to {PLANS[suggested].name} – {PLANS[suggested].priceLabel}/month
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <button
              className="text-sm text-foreground/50 hover:text-foreground/70 py-2 transition-colors"
              onClick={() => setShowOutOfCredits(false)}
            >
              Maybe later
            </button>
          </div>
        </div>
      </Dialog>

      {/* Paywall: download episode */}
      <UpgradeDialog
        open={showDownloadPaywall}
        onOpenChange={setShowDownloadPaywall}
        title="Keep this episode forever"
        description={undefined}
        bullets={[
          "High-quality MP3 export",
          "Keep files forever, even if you cancel",
          "Works offline in any podcast app",
          "Archive your best episodes"
        ]}
        paywallId="download_episode"
      />
    </div>
  )
}